import React, { FC, ReactNode, useMemo } from "react";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import { da } from "date-fns/locale/da";
import { de } from "date-fns/locale/de";
import { enUS } from "date-fns/locale/en-US";
import { es } from "date-fns/locale/es";
import { fr } from "date-fns/locale/fr";
import { nb } from "date-fns/locale/nb";
import { nl } from "date-fns/locale/nl";
import { sv } from "date-fns/locale/sv";
import {
  createGettext,
  globalGettext,
  type SprintfArg,
} from "@absolutesight/gettext";
import { GettextProvider } from "@absolutesight/gettext/react";
import moment from "moment";
import "moment/locale/de";
import "moment/locale/da";
import "moment/locale/en-gb";
import "moment/locale/es";
import "moment/locale/fr";
import "moment/locale/nb";
import "moment/locale/nl";
import "moment/locale/sv";
import { useLang } from "./useLang";
import {
  TextDomainContext,
  type TextDomainContextValue,
} from "./useTextDomainContext";

// Register locales
registerLocale("da", da);
registerLocale("de", de);
registerLocale("en", enUS);
registerLocale("es", es);
registerLocale("fr", fr);
registerLocale("nb", nb);
registerLocale("nl", nl);
registerLocale("no", nb);
registerLocale("sv", sv);

interface I18nProviderProps {
  children: ReactNode;
}

interface Profile {
  catalog: Record<string, string>;
  plural: string;
}

const translations = import.meta.glob<Record<string, unknown>>(
  "../../../languages/LC_MESSAGES/*.mo.json",
  { eager: true },
);

/**
 * Provider component for internationalization (i18n) context powered by @absolutesight/gettext
 * @param {Object} props - Component props
 * @param {ReactNode} props.children - Child components
 */
export const I18nProvider: FC<I18nProviderProps> = ({ children }) => {
  const locale = useLang();

  // Configure moment and react-datepicker locales
  const momentLocale = useMemo(() => {
    switch (locale) {
      case "en":
        return "en-gb";
      case "no":
        return "nb";
      default:
        return locale;
    }
  }, [locale]);

  // Build isolated Gettext instance and load catalog for the current locale
  const i18n = useMemo(() => {
    const inst = createGettext({ locale });

    const path = `../../../languages/LC_MESSAGES/${locale}.mo.json`;
    let profile: Profile = { catalog: {}, plural: "n != 1" };

    if (translations[path]) {
      profile = translations[path] as unknown as Profile;
    }

    const pluralHeader = profile.plural
      ? `nplurals=2; plural=${profile.plural};`
      : undefined;

    const catalogData = {
      headers: pluralHeader ? { "plural-forms": pluralHeader } : {},
      pluralForms: pluralHeader,
      entries: profile.catalog || {},
    };

    inst.loadTextdomain("default", catalogData, locale);

    // Also synchronize globalGettext for any out-of-React functions
    globalGettext.setLocale(locale);
    globalGettext.loadTextdomain("default", catalogData, locale);

    setDefaultLocale(locale);
    moment.locale(momentLocale);

    return inst;
  }, [locale, momentLocale]);

  // Context value providing both legacy gettext() compatibility and full WordPress functions
  const textDomainValue: TextDomainContextValue = useMemo(() => {
    return {
      gettext: (text: string, domain?: string) => i18n.__(text, domain),
      ngettext: (
        single: string,
        plural: string,
        number: number,
        domain?: string,
      ) => i18n._n(single, plural, number, domain),
      xgettext: (text: string, context: string, domain?: string) =>
        i18n._x(text, context, domain),
      nxgettext: (
        single: string,
        plural: string,
        number: number,
        context: string,
        domain?: string,
      ) => i18n._nx(single, plural, number, context, domain),
      __: (text: string, domain?: string) => i18n.__(text, domain),
      _e: (text: string, domain?: string) => i18n._e(text, domain),
      _x: (text: string, context: string, domain?: string) =>
        i18n._x(text, context, domain),
      _ex: (text: string, context: string, domain?: string) =>
        i18n._ex(text, context, domain),
      _n: (single: string, plural: string, number: number, domain?: string) =>
        i18n._n(single, plural, number, domain),
      _nx: (
        single: string,
        plural: string,
        number: number,
        context: string,
        domain?: string,
      ) => i18n._nx(single, plural, number, context, domain),
      sprintf: (format: string, ...args: SprintfArg[]) =>
        i18n.sprintf(format, ...args),
      sprintfReact: (format: string, ...args: SprintfArg[]) =>
        i18n.sprintfReact(format, ...args),
      i18n,
    };
  }, [i18n]);

  return (
    <GettextProvider instance={i18n} locale={locale}>
      <TextDomainContext.Provider value={textDomainValue}>
        {children}
      </TextDomainContext.Provider>
    </GettextProvider>
  );
};
